if (customElements.get('atc-summary') === undefined) {
  class AtcSummary extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.update = this.update.bind(this);
    }

    connectedCallback() {
      this.retailEl = this.querySelector('[data-atc-retail]');
      this.saveEl = this.querySelector('[data-atc-save]');
      if (!this.retailEl || !this.saveEl) {
        if (typeof console !== 'undefined') {
          console.warn('[atc-summary] missing required child elements', {
            retail: !!this.retailEl,
            save: !!this.saveEl,
          });
        }
        return;
      }

      // Section scope, never `document`. The gate in update() is decided by whether a
      // checked concealer-amount exists, so a document-wide lookup lets a second product
      // section (quiz upsell, a second buy box) flip this strip's branch and print another
      // product's totals. Wrong numbers next to a real price, with nothing in the console.
      // Same rule as autoship-toggle.js and shade-selected-display-v2.js.
      this.scope = this.closest('.aiw-v2-main-product') || this.closest('.main-product-v2');
      if (!this.scope) {
        console.warn('[atc-summary] no recognized product section ancestor — savings strip hidden');
        this.style.display = 'none';
        return;
      }
      // main-product-v2 marker. Only meaningful together with "is there an amount
      // selector?" — see update(). Don't treat it as "no amount selector on its own":
      // the concealer-variant picker block puts one on this section too.
      this.isV3 = !!this.closest('.main-product-v2');
      document.addEventListener('change', this.onChange);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
    }

    onChange(event) {
      const t = event.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (t.matches('input[name="concealer-amount"], input[name="purchase_option"]')) {
        this.update();
      }
    }

    moneyFormat(cents) {
      const n = parseInt(cents, 10);
      if (isNaN(n)) return '';
      // Use Shopify.formatMoney so the currency symbol respects shop locale
      // (US store renders $, UK store renders £, etc. — no hardcoded symbol).
      // Strip trailing .00 / ,00 to match Liquid's money_without_trailing_zeros filter.
      const formatted = (window.Shopify && Shopify.formatMoney) ? Shopify.formatMoney(n) : '$' + (n / 100).toFixed(2);
      return formatted.replace(/([.,])00(?!\d)/, '');
    }

    getCheckedAmount() {
      return this.scope.querySelector('input[name="concealer-amount"]:checked');
    }

    getCheckedPurchaseOption() {
      return this.scope.querySelector('input[name="purchase_option"]:checked');
    }

    update() {
      const amount = this.getCheckedAmount();

      // main-product-v2 WITHOUT an amount selector (e.g. MoistureWiser): there are no
      // per-tier totals to read, so derive retail + savings from the selected purchase
      // option (data-price / data-compare-at-price, per-quantity cents).
      //
      // Gate on the absence of `amount`, NOT on the section class alone. The
      // concealer-variant picker block renders input[name="concealer-amount"] on this
      // same section, and its per-tier data-atc-* values must win — otherwise the strip
      // shows single-unit numbers that contradict the "SAVE n%" badge above it, or
      // hides outright when the product has no compare-at price.
      if (this.isV3 && !amount) {
        const po = this.getCheckedPurchaseOption();
        if (!po) {
          // Should not happen — the snippet marks a default option checked. If it does,
          // the strip is hidden, but surface it so drifted markup is debuggable.
          console.warn('[atc-summary] no checked purchase_option — savings strip hidden');
          this.style.display = 'none';
          return;
        }

        const nowCents = parseInt(po.dataset.price, 10);
        const retailCents = parseInt(po.dataset.compareAtPrice, 10);
        if (isNaN(nowCents) || isNaN(retailCents)) {
          // An empty compare-at is the legitimate one-time case (hide, no noise). A
          // present-but-unparseable price is bad data worth surfacing.
          if (po.dataset.compareAtPrice) {
            console.warn('[atc-summary] unparseable price data on purchase option', {
              price: po.dataset.price,
              compareAt: po.dataset.compareAtPrice,
            });
          }
          this.style.display = 'none';
          return;
        }

        if (retailCents > nowCents) {
          const saveCents = retailCents - nowCents;
          const savePct = Math.round((saveCents / retailCents) * 100);
          this.retailEl.textContent = this.moneyFormat(retailCents);
          this.saveEl.textContent = this.moneyFormat(saveCents) + ' (' + savePct + '%)';
          this.style.display = '';
        } else {
          // One-time / no savings → hide the strip (matches the offer mockup).
          this.style.display = 'none';
        }
        return;
      }

      if (!amount) {
        // AIW-style sections render a default-checked amount radio; if it's
        // missing the markup drifted. Hide rather than show wrong numbers.
        console.warn('[atc-summary] no checked concealer-amount — savings strip hidden');
        this.style.display = 'none';
        return;
      }

      const basePriceCents = parseInt(amount.dataset.price, 10);
      const retailCents = parseInt(amount.dataset.atcRetailCents, 10);

      const po = this.getCheckedPurchaseOption();
      const pct = po ? parseFloat(po.dataset.discountPercentage || '0') : 0;

      if (!isNaN(basePriceCents) && !isNaN(retailCents) && retailCents > 0) {
        // Run for pct === 0 too, so a tier with no subscription discount is still
        // evaluated and can be hidden below. `pct > 0` used to skip straight to the
        // Liquid-rendered fallback strings, which happily printed "$0 (0%)".
        const discountedCents = pct > 0 ? Math.round((basePriceCents * (100 - pct)) / 100) : basePriceCents;
        const saveCents = retailCents - discountedCents;

        // Nothing saved (e.g. Buy-1 at one-time price, where retail === price) — a
        // "Retail value" line with an identical number and "YOU SAVE $0 (0%)" reads as
        // broken, so hide the whole strip rather than print a no-op.
        if (saveCents <= 0) {
          this.style.display = 'none';
          return;
        }

        const savePct = Math.round((saveCents / retailCents) * 100);
        this.retailEl.textContent = this.moneyFormat(retailCents);
        this.saveEl.textContent = this.moneyFormat(saveCents) + ' (' + savePct + '%)';
        this.style.display = '';
        return;
      }

      // Fallback for markup that doesn't emit data-atc-retail-cents. Both concealer
      // pickers do emit it, so this is drift-only — it can't do the zero-saving check
      // above (the strings are already money-formatted and locale-dependent), so it
      // just prints what Liquid computed, unchanged from before.
      const retail = amount.dataset.atcRetail;
      const save = amount.dataset.atcSave;
      if (retail) this.retailEl.textContent = retail;
      if (save) this.saveEl.textContent = save;
    }
  }

  customElements.define('atc-summary', AtcSummary);
}
